import Button from "@/components/bricks/button/Button";
import ArrowLeftIcon from "../../components/icons/ArrowLeftIcon";
import CheckIcon from "../../components/icons/CheckIcon";
import { useContext, useState } from "react";
import {
  DropdownContent,
  DropdownItem,
  DropdownItemIcon,
  DropdownRoot,
  DropdownTrigger,
} from "../../components/dropdown/Dropdown";
import {
  TabsContent,
  TabsList,
  TabsRoot,
  TabsTrigger,
} from "../../components/tabs/Tabs";
import PageTitle from "@/components/PageTitle";
import KursusStamoplysninger from "../../components/pages/kursus/id/Stamoplysninger/KursusStamoplysninger";
import KursusDeltagere from "../../components/pages/kursus/id/Deltagere/KursusDeltagere";
import KursusKorrespondance from "../../components/pages/kursus/id/KursusKorrespondance";
import KursusIcon from "../../components/icons/KursusIcon";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@apollo/client";
import Spinner from "../../components/spinner/Spinner";
import { GET_KURSUSHOLD_BY_ID_QUERY } from "../../api/kursus/queries";
import { PagedKursusQueryContext } from "../../context/PagedKursusQueryProvider";
import Sidebar from "../../components/pages/kursus/id/Sidebar";
import { IDPagination } from "../../components/pagination/IDPagination";
import { ErrorBoundary } from "react-error-boundary";
import ErrorFallback from "../../components/ErrorFallback";
import {
  BestaaetStatusId,
  DeltagerStatusId,
  Kursushold,
} from "../../interfaces/schema";
import { dateToyyyyMMdd } from "../../util/date";
import { UserQueryContext } from "../../context/UserContext";
import AccessDenied from "../../components/AccessDenied";
import ReportIcon from "../../components/icons/ReportIcon";
import { ReportLink } from "../../components/ReportLink";
import { useReports, type Report } from "../../hooks/useQueries";
import { Notification, useToaster } from "rsuite";
import DefaultLoadingError from "../../components/error/DefaultLoadingError";
import { transformQueryDataPerioder } from "../../util/apollo";

const KursusById = () => {
  // navigation
  const params = useParams();
  const { id } = params;
  const navigate = useNavigate();

  // context
  const pagedKursusQueryContext = useContext(PagedKursusQueryContext);
  const { user, hasPermission } = useContext(UserQueryContext);

  // hooks and things
  const { reports: allReports } = useReports();
  const reports = allReports?.filter((r) => r.uiPath === "aika/web/kursus");
  reports?.sort((a, b) => a.title.localeCompare(b.title));

  // load some stuff
  const [data, setData] = useState<
    | {
        kursushold: Kursushold;
      }
    | undefined
  >(undefined);
  const { loading, error, refetch } = useQuery<{
    kursushold: Kursushold;
  }>(GET_KURSUSHOLD_BY_ID_QUERY(), {
    variables: { id, date: dateToyyyyMMdd(new Date()) },
    notifyOnNetworkStatusChange: true,
    onCompleted: (d) => {
      const transformed = transformQueryDataPerioder(d);
      setData(transformed);
    },
  });
  if (loading) return <Spinner />;

  if (error) {
    return (
      <div className="d-flex flex-row align-items-center justify-content-center flex-grow-1">
        <DefaultLoadingError
          label="Der skete en fejl da vi hentede kursusholdet .."
          refreshLabel="Prøv igen"
          onRefresh={() => refetch}
        />
      </div>
    );
  }
  if (!data || !id) return null;
  const { kursushold } = data;
  if (!kursushold) return <div>Intet kursushold fundet 😯</div>;

  return (
    <div className="l-main__content">
      <PageTitle title={`${kursushold.navn} - kursus`} />{" "}
      <ErrorBoundary FallbackComponent={ErrorFallback}>
        <header className="navbar navbar--sub">
          <div className="col d-flex align-items-center gap-3">
            <Button
              icon
              color="secondary"
              aria-label="Tilbage"
              onClick={() => {
                navigate("/kursus");
              }}
            >
              <ArrowLeftIcon />
            </Button>
            <h2 className="mx-1 d-flex align-items-center gap-2">
              <span className="me-1 mt-n1">
                <KursusIcon height={26} />
              </span>
              {kursushold.navn}{" "}
              <span className="text-secondary h5 align-self-end fw-semibold lh-base">
                Kursus
              </span>
            </h2>
          </div>
          <div className="col d-flex align-items-center justify-content-end gap-5">
            <DropdownRoot className="c-dropdown-default--actions">
              <DropdownTrigger>
                <span className="badge badge--fixed-size ms-n2">
                  <span className="ms-n2">
                    <CheckIcon />
                  </span>
                  <span>Aktuelt kursus</span>
                </span>
                <span className="fw-semibold">Handlinger</span>
              </DropdownTrigger>
              <DropdownContent>
                {reports && (
                  <>
                    {reports &&
                      reports.map((r) => {
                        return (
                          <DropdownItem asChild key={r.id}>
                            {r.requiresBestaaetConfirmation ? (
                              <BestaaetBevisDropdownItem
                                report={r}
                                kursushold={kursushold}
                              />
                            ) : r.title.includes("(Eksamen)") ? (
                              <EksamenDropdownItem
                                report={r}
                                kursushold={kursushold}
                              />
                            ) : (
                              <ReportLink
                                url={`${r.url}?id=${kursushold.id}`}
                                className="c-dropdown-default-item"
                              >
                                <DropdownItemIcon>
                                  <ReportIcon />
                                </DropdownItemIcon>
                                <span>{r.title}</span>
                              </ReportLink>
                            )}
                          </DropdownItem>
                        );
                      })}
                  </>
                )}
              </DropdownContent>
            </DropdownRoot>
            <IDPagination
              pagedContext={pagedKursusQueryContext}
              currentSelection={kursushold}
            />
          </div>
        </header>
        <div className="l-main-row">
          <Sidebar kursushold={kursushold} />
          <main className="col overflow-hidden">
            <TabsRoot
              defaultValue="stamoplysninger"
              localStorageKey="kursus-id-tabs"
            >
              <TabsList>
                <div className="container">
                  <TabsTrigger
                    value="stamoplysninger"
                    isDisabled={!hasPermission(user, "KursusRead")}
                  >
                    Stamoplysninger
                  </TabsTrigger>
                  <TabsTrigger
                    value="deltagere"
                    isDisabled={!hasPermission(user, "KursusRead")}
                  >
                    Deltagere
                  </TabsTrigger>
                  <TabsTrigger
                    value="korrespondance"
                    isDisabled={!hasPermission(user, "KursusRead")}
                  >
                    Korrespondance
                  </TabsTrigger>
                </div>
              </TabsList>
              <TabsContent value="stamoplysninger">
                {hasPermission(user, "KursusRead") ? (
                  <KursusStamoplysninger
                    kursushold={kursushold}
                    refetch={refetch}
                  />
                ) : (
                  <AccessDenied />
                )}
              </TabsContent>
              <TabsContent value="deltagere" className="overflow-hidden">
                {hasPermission(user, "KursusRead") ? (
                  <KursusDeltagere id={id} kursushold={kursushold} />
                ) : (
                  <AccessDenied />
                )}
              </TabsContent>
              <TabsContent value="korrespondance">
                {hasPermission(user, "KursusRead") ? (
                  <KursusKorrespondance kursusholdId={kursushold.id} />
                ) : (
                  <AccessDenied />
                )}
              </TabsContent>
            </TabsRoot>
          </main>
        </div>
      </ErrorBoundary>
    </div>
  );
};

export default KursusById;

function EksamenDropdownItem({
  report,
  kursushold,
}: {
  report: { title: string; url: string };
  kursushold: Kursushold;
}) {
  if (kursushold.kursustypeNavigation?.navn !== "FMU Prøve") {
    return (
      <span
        title="Kun for FMU Prøver"
        style={{ cursor: "auto" }}
        className="c-dropdown-default-item disabled"
      >
        {" "}
        <DropdownItemIcon>
          <ReportIcon />
        </DropdownItemIcon>
        <span>{report.title}</span>
      </span>
    );
  }

  return (
    <ReportLink
      url={`${report.url}?id=${kursushold.id}`}
      className="c-dropdown-default-item"
    >
      <DropdownItemIcon>
        <ReportIcon />
      </DropdownItemIcon>
      <span>{report.title}</span>
    </ReportLink>
  );
}

// Bevis-rapporter (requiresBestaaetConfirmation): udsteder kun bevis til beståede kursister.
// Bloker generering hvis ingen er markeret som bestået — ellers ville vi udstede et tomt bevis.
// (Bemærk: på FMU Prøve-hold defaulter tilmelding BestaaetMark til Nej, så "ikke bedømt" findes
// ikke som en egen tilstand — derfor ingen bekræftelses-dialog for ubedømte, kun zero-Ja-stop.)
// Genbruger ReportLink, så loading/busy-tilstand er ens med de øvrige rapporter.
function BestaaetBevisDropdownItem({
  report,
  kursushold,
}: {
  report: Report;
  kursushold: Kursushold;
}) {
  const toaster = useToaster();

  // Tæl ud fra samme kursusPerson-data som deltagerlisten viser.
  const bestaaetAntal = (kursushold.kursusPerson ?? []).filter(
    (kp) =>
      kp.deltagerStatus === DeltagerStatusId.Tilmeldt &&
      kp.bestaaetMark === BestaaetStatusId.Ja,
  ).length;

  return (
    <ReportLink
      url={`${report.url}?id=${kursushold.id}`}
      className="c-dropdown-default-item"
      onBeforeOpen={() => {
        if (bestaaetAntal === 0) {
          toaster.push(
            <Notification type="warning" header="Ingen beståede">
              Ingen kursister er markeret som bestået. Der er intet
              eksamensbevis at generere.
            </Notification>,
            { placement: "topEnd", duration: 6000 },
          );
          return false;
        }
        return true;
      }}
    >
      <DropdownItemIcon>
        <ReportIcon />
      </DropdownItemIcon>
      <span>{report.title}</span>
    </ReportLink>
  );
}
